import { useState } from "react";
import { useParams, Link } from "wouter";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, MapPin, Briefcase, Lock, BarChart3, TrendingUp, ShieldCheck, ChevronRight, Tag, Share2, Check, FileText, X } from "lucide-react";
import sampleReportImage from "@assets/Screenshot_2026-03-18_101955_1773815270447.jpg";
import companiesData from "@/data/companies.json";
import { findCompanyBySlug, slugify } from "@/lib/slugify";
import { useTranslations } from "@/lib/translations";
import LeadFormModal, { type LeadContextItem } from "@/components/LeadFormModal";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";
import { buildCorporationJsonLd } from "@/lib/company-jsonld";
import { pushEvent } from "@/lib/analytics";

interface Company {
  Name: string;
  "Short Name": string;
  Isin: string;
  Country: string;
  Sector: string;
}

const companies: Company[] = companiesData as Company[];

const countryAr: Record<string, string> = {
  "Bahrain": "البحرين",
  "Jordan": "الأردن",
  "Kuwait": "الكويت",
  "Lebanon": "لبنان",
  "Oman": "عُمان",
  "Palestine": "فلسطين",
  "Qatar": "قطر",
  "Saudi Arabia": "المملكة العربية السعودية",
  "United Arab Emirates": "الإمارات العربية المتحدة",
};

const sectorAr: Record<string, string> = {
  "Banks": "البنوك",
  "Business Support Services": "خدمات دعم الأعمال",
  "Chemicals": "الكيماويات",
  "Construction & Engineering": "الإنشاء والهندسة",
  "Construction Materials": "مواد البناء",
  "Consumer Durables": "السلع المعمرة",
  "Containers & Packaging": "الحاويات والتعبئة",
  "Diversified Financial Services": "الخدمات المالية المتنوعة",
  "Education": "التعليم",
  "Electrical Components and Equipment": "المكونات والمعدات الكهربائية",
  "Energy": "الطاقة",
  "Food and Beverages": "الأغذية والمشروبات",
  "Health Care": "الرعاية الصحية",
  "Hotels and Leisure": "الفنادق والترفيه",
  "Industrial Conglomerates": "المجمعات الصناعية",
  "Insurance": "التأمين",
  "Media and Publishing": "الإعلام والنشر",
  "Metals and Mining": "المعادن والتعدين",
  "Paper & Forest Products": "منتجات الورق والغابات",
  "REITS": "صناديق الاستثمار العقاري",
  "Real Estate": "العقارات",
  "Retail": "التجزئة",
  "Software": "البرمجيات",
  "Telecommunication Services": "خدمات الاتصالات",
  "Tobacco": "التبغ",
  "Transportation and Logistics": "النقل واللوجستيات",
  "Travel and Tourism": "السفر والسياحة",
  "Utilities": "المرافق العامة",
};

const CompanyProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const company = findCompanyBySlug(companies, slug ?? "");
  const [copied, setCopied] = useState(false);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const { language, isRTL } = useTranslations();
  const isAr = language === "ar";

  const handleShare = async () => {
    const url = window.location.href;
    const title = company ? `${company.Name} ESG Profile | ESG Invest` : "ESG Invest";
    const text = company
      ? `Check out the ESG profile for ${company.Name} (${company?.["Short Name"]}) on ESG Invest — Middle East's leading ESG intelligence platform.`
      : "";
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (_) {}
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const meta = company
    ? {
        title: `${company.Name} — ESG Rating & Score | ESG Invest`,
        description: `View the ESG rating for ${company.Name} (${company.Country}, ${company.Sector}). Environmental, Social & Governance scores plus 43-criterion breakdown from ESG Invest.`,
        path: `/companies/${slugify(company.Name)}`,
        type: "profile" as const,
      }
    : {
        title: "Company Not Found | ESG Invest",
        description:
          "Company not found on ESG Invest. Browse 880+ Middle East listed companies with independent ESG ratings, sector-adjusted scores, and pillar-level analysis.",
        path: "/companies",
      };

  usePageMeta(meta);

  useJsonLd(
    "ld-company-breadcrumbs",
    buildBreadcrumbList(
      company
        ? [
            { name: "Home", path: "/" },
            { name: "Companies", path: "/companies" },
            { name: company.Name, path: `/companies/${slugify(company.Name)}` },
          ]
        : [
            { name: "Home", path: "/" },
            { name: "Companies", path: "/companies" },
          ],
    ),
  );

  useJsonLd("ld-company", company ? buildCorporationJsonLd(company) : null);

  if (!company) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <p className="text-xl font-semibold text-foreground">
            {isAr ? "الشركة غير موجودة" : "Company not found"}
          </p>
          <Link href="/companies">
            <Button variant="outline">
              {isAr ? "العودة إلى الشركات" : "Back to Companies"}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const displayCountry = isAr ? (countryAr[company.Country] ?? company.Country) : company.Country;
  const displaySector = isAr ? (sectorAr[company.Sector] ?? company.Sector) : company.Sector;

  const leadContextItems: LeadContextItem[] = [
    { label: isAr ? "الشركة" : "Company", value: company.Name },
    { label: isAr ? "الدولة" : "Country", value: displayCountry },
    { label: isAr ? "القطاع" : "Sector", value: displaySector },
    { label: isAr ? "السعر التقديري" : "Estimated price", value: "$100" },
  ];
  const leadContextPayload: Record<string, unknown> = {
    plan: "individual_reports",
    companies: [company.Name],
    isin: company.Isin,
    country: company.Country,
    sector: company.Sector,
    reportCount: 1,
    estimatedTotalUsd: 100,
    sourcePage: `company_profile:${slugify(company.Name)}`,
  };

  return (
    <Layout>
      <div className="min-h-screen bg-se-gray-50 dark:bg-background">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-10">

          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-foreground transition-colors">
              {isAr ? "الرئيسية" : "Home"}
            </Link>
            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
            <Link href="/companies" className="hover:text-foreground transition-colors">
              {isAr ? "الشركات" : "Companies"}
            </Link>
            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="text-foreground font-medium truncate" dir="ltr">{company.Name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6 border-0 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="bg-accent/10 p-3 rounded-xl flex-shrink-0">
                    <Building2 className="h-8 w-8 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0" dir="ltr">
                    <h1 className="text-2xl font-bold text-foreground leading-tight">
                      {isAr
                        ? `${company.Name} — تصنيف ودرجة ESG`
                        : `${company.Name} — ESG Rating & Score`}
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1 font-medium">{company["Short Name"]}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShare}
                    data-testid="button-share-profile"
                    className="flex items-center gap-2 flex-shrink-0 transition-all"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-green-600 text-xs font-medium">
                          {isAr ? "تم النسخ!" : "Copied!"}
                        </span>
                      </>
                    ) : (
                      <>
                        <Share2 className="h-4 w-4" />
                        <span className="text-xs font-medium">{isAr ? "مشاركة" : "Share"}</span>
                      </>
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
                  <div className="bg-muted/40 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <MapPin className="h-3 w-3" />
                      {isAr ? "الدولة" : "Country"}
                    </div>
                    <p className="font-semibold text-foreground text-sm">{displayCountry}</p>
                  </div>
                  <div className="bg-muted/40 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Briefcase className="h-3 w-3" />
                      {isAr ? "القطاع" : "Sector"}
                    </div>
                    <p className="font-semibold text-foreground text-sm">{displaySector}</p>
                  </div>
                  <div className="bg-muted/40 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Tag className="h-3 w-3" />
                      ISIN
                    </div>
                    <p className="font-semibold text-foreground text-sm font-mono" dir="ltr">{company.Isin}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-0 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-bold text-foreground">
                    {isAr ? "درجات ESG" : "ESG Scores"}
                  </h2>
                  <span className="text-xs text-muted-foreground/60">
                    {isAr ? "الدرجة من 100" : "Score out of 100"}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-muted-foreground w-24">
                      {isAr ? "إجمالي ESG" : "Overall ESG"}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-accent/15 text-accent blur-sm select-none">75</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground w-22">
                      {isAr ? "البيئة" : "Environmental"}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 blur-sm select-none">72</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground w-22">
                      {isAr ? "الاجتماع" : "Social"}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 blur-sm select-none">78</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground w-22">
                      {isAr ? "الحوكمة" : "Governance"}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 blur-sm select-none">80</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-5 border-t border-border pt-4">
                  {isAr
                    ? `اشترك للاطلاع على درجات ESG الكاملة والاتجاهات التاريخية ومقارنة الأداء بالنظراء لـ ${company.Name} وجميع الشركات المغطاة (880+ شركة).`
                    : `Subscribe to unlock full ESG scores, historical trends, and peer benchmarks for ${company.Name} and all 880+ covered companies.`}
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-sm">
                <h2 className="text-base font-bold text-foreground mb-3">
                  {isAr ? `عن ${company.Name}` : `About ${company.Name}`}
                </h2>
                {isAr ? (
                  <>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {company.Name} ({company["Short Name"]}) شركة مدرجة في البورصة في{" "}
                      {displayCountry}، تعمل في قطاع <strong>{displaySector}</strong>.
                      تغطيها منصة ESG Invest للذكاء البيئي والاجتماعي والحوكمي في الشرق الأوسط،
                      التي ترصد أداء أكثر من 880 شركة في دول مجلس التعاون الخليجي ومنطقة الشرق الأوسط وشمال أفريقيا.
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                      تعكس تصنيفات ESG للشركات المدرجة في {displayCountry} المتطلبات التنظيمية المحلية
                      ومعايير الإفصاح الإقليمية والمعايير القطاعية للاستدامة ذات الصلة بالمشهد الاستثماري في الشرق الأوسط.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {company.Name} ({company["Short Name"]}) is a publicly listed company in{" "}
                      {company.Country}, operating in the <strong>{company.Sector}</strong> sector.
                      It is covered by ESG Invest's Middle East ESG intelligence platform, which tracks environmental,
                      social, and governance performance for 880+ companies across the GCC and broader MENA region.
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                      ESG ratings for companies listed in {company.Country} reflect local regulatory requirements,
                      regional disclosure standards, and sector-specific sustainability benchmarks relevant to the
                      Middle East investment landscape.
                    </p>
                  </>
                )}
              </Card>
            </div>

            <div className="flex flex-col gap-4">
              <Card className="p-6 border-0 shadow-sm flex-1">
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="bg-accent/10 p-3 rounded-full">
                    <Lock className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-base">
                      {isAr ? "الملف الكامل لـ ESG" : "Full ESG Profile"}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {isAr
                        ? "اكشف عن الدرجات الكاملة وتفاصيل الركائز والاتجاهات التاريخية ومقارنات النظراء."
                        : "Unlock complete scores, pillar breakdowns, historical trends, and peer benchmarks."}
                    </p>
                  </div>

                  <div className="w-full grid grid-cols-3 gap-2">
                    {[
                      { icon: BarChart3, label: isAr ? "الدرجة الكاملة" : "Full Score" },
                      { icon: TrendingUp, label: isAr ? "الاتجاهات" : "Trends" },
                      { icon: ShieldCheck, label: isAr ? "الركائز" : "Pillars" },
                    ].map(({ icon: Icon, label }) => (
                      <div key={label} className="flex flex-col items-center gap-1.5 bg-muted/50 rounded-xl p-2.5">
                        <Icon className="h-4 w-4 text-accent" />
                        <span className="text-xs font-medium text-foreground text-center leading-tight">{label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="w-full flex flex-col gap-2">
                    <Button variant="accent" size="lg" className="w-full" asChild data-testid="button-subscribe-profile">
                      <Link
                        href="/subscriptions"
                        onClick={() => pushEvent('cta_subscribe', { location: 'company_profile', company_slug: slugify(company.Name) })}
                      >
                        {isAr ? "اشترك للوصول الكامل" : "Subscribe for Full Access"}
                      </Link>
                    </Button>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground">{isAr ? "أو" : "or"}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full"
                      data-testid="button-buy-report-profile"
                      onClick={() => setShowLeadModal(true)}
                    >
                      {isAr ? "شراء تقرير مستقل" : "Purchase Individual Report"}
                    </Button>
                    <button
                      onClick={() => {
                        pushEvent('download_sample_report', { location: 'company_profile', company_slug: slugify(company.Name) });
                        setShowSampleReport(true);
                      }}
                      className="flex items-center justify-center gap-2 text-sm text-accent hover:underline mt-1 cursor-pointer"
                      data-testid="link-sample-report-profile"
                    >
                      <FileText className="h-4 w-4" />
                      {isAr ? "معاينة تقرير ESG" : "Preview ESG Report"}
                    </button>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border-0 shadow-sm flex-1">
                <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                  {isAr ? "شركات أخرى" : "More Companies"}
                </p>
                <div className="space-y-1">
                  <Link href="/companies" className="flex items-center justify-between text-sm text-muted-foreground hover:text-foreground py-1.5 transition-colors group">
                    <span>{isAr ? "تصفح جميع الشركات" : "Browse all companies"}</span>
                    <ChevronRight className={`h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform ${isRTL ? "rotate-180" : ""}`} />
                  </Link>
                  <Link href={`/companies?country=${encodeURIComponent(company.Country)}`} className="flex items-center justify-between text-sm text-muted-foreground hover:text-foreground py-1.5 transition-colors group">
                    <span>{isAr ? `شركات في ${displayCountry}` : `Companies in ${company.Country}`}</span>
                    <ChevronRight className={`h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform ${isRTL ? "rotate-180" : ""}`} />
                  </Link>
                  <Link href={`/companies?sector=${encodeURIComponent(company.Sector)}`} className="flex items-center justify-between text-sm text-muted-foreground hover:text-foreground py-1.5 transition-colors group">
                    <span>{displaySector}</span>
                    <ChevronRight className={`h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform ${isRTL ? "rotate-180" : ""}`} />
                  </Link>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
      {showSampleReport && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowSampleReport(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-[10000]"
            onClick={() => setShowSampleReport(false)}
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={sampleReportImage}
            alt="Sample ESG Report"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <LeadFormModal
        open={showLeadModal}
        onOpenChange={setShowLeadModal}
        source="individual_purchase_report"
        title={isAr ? "شراء تقارير ESG" : "Purchase ESG report(s)"}
        description={
          isAr
            ? "أكّد اختياراتك وسيتواصل معك فريقنا بتعليمات الدفع وتفاصيل التسليم."
            : "Confirm your selections and our team will follow up with payment instructions and delivery details."
        }
        contextItems={leadContextItems}
        contextPayload={leadContextPayload}
      />
    </Layout>
  );
};

export default CompanyProfile;
